import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, User, Calendar, TrendingUp, Award, Clock, Target } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/projectService';
import { phaseService } from '../../services/phaseService';
import { supabase } from '../../lib/supabase';
import HorizontalTimeline from './components/HorizontalTimeline';

const ClientProjectView = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (profile?.role !== 'cliente') {
        navigate('/admin');
      } else {
        loadProjects();
      }
    }
  }, [user, profile, authLoading, navigate]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService?.getProjectsByClient(user?.id);
      setProjects(data);
      
      if (data?.length > 0) {
        setSelectedProject(data?.[0]);
        await loadPhases(data?.[0]?.id);
      }
      
      setError('');
    } catch (err) {
      setError('Erro ao carregar projetos: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPhases = async (projectId) => {
    try {
      const data = await phaseService?.getPhasesByProject(projectId);
      setPhases(data);
    } catch (err) {
      setError('Erro ao carregar fases: ' + err?.message);
    }
  };

  // Real-time subscription for phase changes
  useEffect(() => {
    if (!selectedProject?.id) return;

    // Subscribe to changes in project_phases table for current project
    const channel = supabase
      ?.channel(`project-phases-${selectedProject?.id}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_phases',
          filter: `project_id=eq.${selectedProject?.id}`
        },
        (payload) => {
          console.log('Real-time phase update:', payload);
          
          // Handle different event types
          if (payload?.eventType === 'INSERT') {
            // New phase added - convert to camelCase
            const newPhase = {
              id: payload?.new?.id,
              projectId: payload?.new?.project_id,
              name: payload?.new?.name,
              description: payload?.new?.description,
              phaseOrder: payload?.new?.phase_order,
              startDate: payload?.new?.start_date,
              estimatedEndDate: payload?.new?.estimated_end_date,
              actualEndDate: payload?.new?.actual_end_date,
              status: payload?.new?.status,
              progressPercentage: payload?.new?.progress_percentage,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            };
            setPhases(prev => [...prev, newPhase]?.sort((a, b) => a?.phaseOrder - b?.phaseOrder));
          } else if (payload?.eventType === 'UPDATE') {
            // Phase updated - convert to camelCase
            const updatedPhase = {
              id: payload?.new?.id,
              projectId: payload?.new?.project_id,
              name: payload?.new?.name,
              description: payload?.new?.description,
              phaseOrder: payload?.new?.phase_order,
              startDate: payload?.new?.start_date,
              estimatedEndDate: payload?.new?.estimated_end_date,
              actualEndDate: payload?.new?.actual_end_date,
              status: payload?.new?.status,
              progressPercentage: payload?.new?.progress_percentage,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            };
            setPhases(prev => prev?.map(phase => 
              phase?.id === updatedPhase?.id ? updatedPhase : phase
            ));
          } else if (payload?.eventType === 'DELETE') {
            // Phase deleted
            setPhases(prev => prev?.filter(phase => phase?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    // Cleanup subscription on unmount or project change
    return () => {
      supabase?.removeChannel(channel);
    };
  }, [selectedProject?.id]);

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    await loadPhases(project?.id);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  // Calculate project statistics
  const calculateProjectStats = () => {
    if (!phases?.length) return { total: 0, completed: 0, inProgress: 0, overall: 0 };
    
    const total = phases?.length;
    const completed = phases?.filter(p => p?.status === 'concluido')?.length;
    const inProgress = phases?.filter(p => p?.status === 'em_andamento')?.length;
    const overall = Math.round((completed / total) * 100);
    
    return { total, completed, inProgress, overall };
  };

  const stats = calculateProjectStats();

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-200 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-t-4 border-indigo-600 animate-spin"></div>
          </div>
          <p className="text-base sm:text-lg font-medium text-slate-700">Carregando seu projeto...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Modern Header with Glassmorphism - Mobile Responsive */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-5">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                <div className="relative p-2 sm:p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl sm:rounded-2xl shadow-lg">
                  <Building2 className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                </div>
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent truncate">
                  CTOBRA
                </h1>
                <p className="text-xs sm:text-sm text-slate-600 font-medium hidden sm:block">Gestão de Obras Premium</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden md:flex items-center gap-3 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl sm:rounded-2xl border border-slate-200 shadow-sm">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg sm:rounded-xl">
                  <User className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-slate-700 truncate max-w-[150px]">
                  {profile?.fullName || profile?.email}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className="group flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-br from-rose-50 to-pink-50 hover:from-rose-100 hover:to-pink-100 text-rose-600 rounded-xl sm:rounded-2xl transition-all duration-300 border border-rose-200 shadow-sm hover:shadow-md"
              >
                <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:rotate-12 transition-transform duration-300" />
                <span className="text-xs sm:text-sm font-semibold hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        {projects?.length === 0 ? (
          <div className="text-center py-12 sm:py-20 bg-white/60 backdrop-blur-sm border border-slate-200 rounded-2xl sm:rounded-3xl shadow-xl mx-2">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl sm:rounded-3xl animate-pulse"></div>
              <div className="relative flex items-center justify-center w-full h-full">
                <Building2 className="h-10 w-10 sm:h-12 sm:w-12 text-slate-500" />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2 sm:mb-3 px-4">
              Nenhum projeto atribuído
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-md mx-auto px-4">
              Entre em contato com o administrador para ter acesso aos seus projetos e começar o acompanhamento
            </p>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {/* Project Selector with Modern Design - Mobile Responsive */}
            {projects?.length > 1 && (
              <div className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                <label className="flex items-center gap-2 text-sm sm:text-base font-bold text-slate-800 mb-3 sm:mb-4">
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                  Selecione o Projeto
                </label>
                <select
                  value={selectedProject?.id || ''}
                  onChange={(e) => {
                    const project = projects?.find(p => p?.id === e?.target?.value);
                    if (project) handleProjectSelect(project);
                  }}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-white border-2 border-slate-200 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-400 text-sm sm:text-base text-slate-800 font-medium shadow-sm hover:shadow-md transition-all duration-300"
                >
                  {projects?.map((project) => (
                    <option key={project?.id} value={project?.id}>
                      {project?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Enhanced Project Info Card - Mobile Responsive */}
            {selectedProject && (
              <div className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0 mb-6 sm:mb-8">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg sm:rounded-xl flex-shrink-0">
                        <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <h2 className="text-xl sm:text-3xl font-bold text-slate-800 break-words">
                        {selectedProject?.name}
                      </h2>
                    </div>
                    {selectedProject?.description && (
                      <p className="text-sm sm:text-lg text-slate-600 leading-relaxed break-words">
                        {selectedProject?.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-700 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold border border-emerald-200 shadow-md self-start flex-shrink-0">
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="whitespace-nowrap">{selectedProject?.status}</span>
                  </div>
                </div>

                {/* Project Statistics Cards - Mobile Responsive Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-indigo-100 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg sm:rounded-xl self-start">
                        <Target className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-slate-600">Total de Fases</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-indigo-600">{stats?.total}</p>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-100 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg sm:rounded-xl self-start">
                        <Award className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-slate-600">Concluídas</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-emerald-600">{stats?.completed}</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg sm:rounded-xl self-start">
                        <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-slate-600">Em Andamento</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600">{stats?.inProgress}</p>
                  </div>

                  <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-violet-100 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-lg sm:rounded-xl self-start">
                        <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-slate-600">Progresso Total</span>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-violet-600">{stats?.overall}%</p>
                  </div>
                </div>

                {/* Project Dates with Enhanced Design - Mobile Responsive Stack */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-sm sm:text-base">
                  <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl sm:rounded-2xl border border-blue-100 shadow-sm">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                    <span className="font-semibold text-slate-700 text-xs sm:text-base">
                      Início: <span className="text-blue-600">{new Date(selectedProject.startDate)?.toLocaleDateString('pt-BR')}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-xl sm:rounded-2xl border border-violet-100 shadow-sm">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-violet-600 flex-shrink-0" />
                    <span className="font-semibold text-slate-700 text-xs sm:text-base">
                      Previsão: <span className="text-violet-600">{new Date(selectedProject.estimatedEndDate)?.toLocaleDateString('pt-BR')}</span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Timeline Section - Mobile Responsive */}
            {selectedProject && phases?.length > 0 && (
              <div className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg sm:rounded-xl">
                    <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-slate-800">
                    Cronograma de Execução
                  </h3>
                </div>
                <HorizontalTimeline phases={phases} />
              </div>
            )}

            {selectedProject && phases?.length === 0 && (
              <div className="text-center py-12 sm:py-16 bg-white/60 backdrop-blur-sm border border-slate-200 rounded-2xl sm:rounded-3xl shadow-xl">
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl sm:rounded-3xl animate-pulse"></div>
                  <div className="relative flex items-center justify-center w-full h-full">
                    <Calendar className="h-8 w-8 sm:h-10 sm:w-10 text-slate-500" />
                  </div>
                </div>
                <p className="text-base sm:text-lg font-medium text-slate-600 px-4">
                  Este projeto ainda não possui etapas cadastradas
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProjectView;